import React, { useRef } from 'react'
import { Autocomplete, TextField } from "@mui/material";
import "pages/Styles/UIKit_v2.sass";

function optionTitle(option: any): string {
    return (typeof option === 'string' ? option : option?.title) ?? ''
}

function clampedOption(lineClamp: number) {
    return (liProps: React.HTMLAttributes<HTMLLIElement> & { key?: React.Key }, option: any) => {
        const { key, ...rest } = liProps
        return (
            <li
                key={key}
                {...rest}
                style={{ ...(rest.style ?? {}), whiteSpace: 'normal', alignItems: 'flex-start' }}
            >
                <span style={{
                    display: '-webkit-box',
                    WebkitBoxOrient: 'vertical',
                    WebkitLineClamp: lineClamp,
                    overflow: 'hidden',
                    whiteSpace: 'normal',
                    overflowWrap: 'anywhere',
                    minWidth: 0,
                    flex: '1 1 auto',
                    lineHeight: '140%',
                }}>
                    {optionTitle(option)}
                </span>
            </li>
        )
    }
}

type SelectOptionsViewProps = {
    selectedOption?: any,
    onSelectOption: (option: any) => void,
    options: Array<{id: string, title: string}>,
    placeholder?: string,
    height?: string,
    onLoadMore?: () => void,
    hasMore?: boolean,
    loading?: boolean,
    /** Только выбор из списка, без ввода текста (для фильтров) */
    freeSolo?: boolean,
    /** Ширина: 'content' — по контенту (длина самой длинной опции), 'full' — 100%, число — px */
    width?: 'content' | 'full' | number,
    /** Доп. стили для выпадающего списка (например zIndex для попапов) */
    slotPropsPaper?: { sx?: object; style?: object },
    /** z-index для Popper (чтобы выпадающий список был поверх попапа) */
    popperZIndex?: number,
    /** Не переворачивать список вверх (регистрация: иначе закрывает заголовок поля) */
    disableFlip?: boolean,
    /** Состояние ошибки валидации */
    error?: boolean,
    /** Ref для внутреннего input (для focus и т.д.) */
    inputRef?: React.RefObject<HTMLInputElement | null>,
    /** При freeSolo: вызывается при вводе текста (для controlled input). Требует inputValue. */
    onInputChange?: (value: string) => void,
    /** При freeSolo + onInputChange: текущее значение input (для controlled) */
    inputValue?: string,
    /** Вызывается при потере фокуса */
    onBlur?: () => void,
    /** Вызывается при получении фокуса */
    onFocus?: () => void,
    disabled?: boolean,
    /** Сколько строк текста показывать у пункта. Хвост обрезается, список скроллится. */
    optionLineClamp?: number
}

function SelectOptionsView({
    selectedOption,
    onSelectOption,
    options,
    placeholder = "Выберите опцию",
    height,
    onLoadMore,
    hasMore = false,
    loading = false,
    freeSolo = true,
    width,
    slotPropsPaper,
    popperZIndex,
    disableFlip = false,
    error = false,
    inputRef,
    onInputChange,
    inputValue,
    onBlur,
    onFocus,
    disabled = false,
    optionLineClamp,
}: SelectOptionsViewProps) {
    const listboxRef = useRef<HTMLUListElement | null>(null);

    const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
        const listboxNode = event.currentTarget;
        const { scrollTop, scrollHeight, clientHeight } = listboxNode;
        
        // Загружаем больше, когда пользователь прокрутил до 80% списка
        if (hasMore && !loading && onLoadMore && scrollTop + clientHeight >= scrollHeight * 0.8) {
            onLoadMore();
        }
    };

    const fitContent = width === 'content';
    const maxCharWidth = Math.max(
        ...options.map((o) => (o?.title ?? '').length),
        (placeholder ?? '').length,
        8
    ) + 1;
    const widthStyle =
        width === 'full' ? { width: '100%' }
        : typeof width === 'number' ? { width: `${width}px` }
        : fitContent ? { width: 'fit-content' }
        : undefined;

    return (
        <div className="select-options-view-v2 ui-kit-v2" style={widthStyle}>
            <Autocomplete
                disabled={disabled}
                freeSolo={freeSolo}
                fullWidth={!fitContent}
                handleHomeEndKeys={true}
                selectOnFocus={freeSolo}
                value={selectedOption ?? null}
                inputValue={onInputChange != null && freeSolo ? (inputValue ?? selectedOption?.title ?? '') : undefined}
                onInputChange={onInputChange != null && freeSolo
                    ? (e, value) => onInputChange(value)
                    : undefined}
                options={options}
                isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                getOptionLabel={optionTitle}
                renderOption={optionLineClamp ? clampedOption(optionLineClamp) : undefined}
                onChange={(e, value) => onSelectOption(value)}
                {...(freeSolo === false && { filterOptions: (opts) => opts })}
                ListboxProps={{ onScroll: handleScroll, ref: listboxRef }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '12px',
                            border: '1px solid #DCE3EB',
                            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                            marginTop: '8px',
                            ...(slotPropsPaper?.sx ?? {})
                        },
                        ...(slotPropsPaper?.style && { style: slotPropsPaper.style })
                    },
                    popper: {
                        placement: 'bottom-start',
                        sx: popperZIndex != null ? { zIndex: popperZIndex } : undefined,
                        modifiers: disableFlip
                            ? [{ name: 'flip', enabled: false }]
                            : undefined,
                    },
                    popupIndicator: { tabIndex: -1 },
                    clearIndicator: { tabIndex: -1 },
                }}
                loading={loading}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        disabled={disabled}
                        inputRef={inputRef ? (el: HTMLInputElement | null) => {
                            const ref = params.InputProps.ref;
                            if (ref) {
                                if (typeof ref === 'function') {
                                    (ref as React.RefCallback<HTMLInputElement>)(el);
                                } else {
                                    (ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
                                }
                            }
                            (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
                        } : params.InputProps.ref}
                        variant="standard"
                        fullWidth
                        error={error}
                        InputProps={{
                            ...params.InputProps,
                            disableUnderline: true,
                            ...(onBlur && {
                                onBlur: (e: React.FocusEvent) => {
                                    const inputProps = params.InputProps as { onBlur?: (e: React.FocusEvent) => void };
                                    inputProps.onBlur?.(e);
                                    onBlur();
                                }
                            }),
                            ...(onFocus && {
                                onFocus: (e: React.FocusEvent) => {
                                    const inputProps = params.InputProps as { onFocus?: (e: React.FocusEvent) => void };
                                    inputProps.onFocus?.(e);
                                    onFocus();
                                }
                            })
                        }}
                        placeholder={placeholder}
                    />
                )}
            />
        </div>
    );
}

export default SelectOptionsView;
